import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import productAll from "../../api/productAll";
import "./Wishlist.css";
import bagIcon from "./../../assets/bagicon.png";
import Swal from "sweetalert2";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useTranslation } from "react-i18next";
import { useSEO } from "../../context/SEOContext";
import { loadProductsWithLangFallback } from "../../utils/productLoader";
const Wishlist = () => {
  const { t, i18n } = useTranslation();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const setSeo = useSEO();
  useEffect(() => {
    setSeo({
      title: t("wishlist.meta.title"),
      description: t("wishlist.meta.description"),
      keywords: t("wishlist.meta.keywords"),
      favicon: "/faviconnew.png", // ✅ burada favicon tanımlandı
    });
  }, [setSeo, t]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("wishlistItems")) || [];
    setWishlistItems(saved);

    const fetchProducts = async () => {
      try {
        const enData = await productAll();
        const localizedData = await loadProductsWithLangFallback(i18n.language);

        // Sadece burada override yap
        const merged = enData.map((item) => {
          const localized = localizedData.find(
            (p) => p.stock_code === item.stock_code
          );
          return {
            ...item,
            ...(localized || {}), // varsa override et
          };
        });

        setAllProducts(merged);
      } catch (err) {
        console.error("Error fetching products", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [i18n.language]);

  const handleWishlistClick = (productId, selectedColor) => (e) => {
    e.preventDefault();
    const key = selectedColor ? `${productId}-${selectedColor}` : productId;
    const storedWishlist =
      JSON.parse(localStorage.getItem("wishlistItems")) || [];
    let updatedWishlist;

    if (storedWishlist.includes(key)) {
      updatedWishlist = storedWishlist.filter((id) => id !== key);
      Swal.fire({
        icon: "success",
        title: t("RemovedWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
        background: "#fff",
        iconColor: "#dc3545",
      });
    } else {
      updatedWishlist = [...storedWishlist, key];
      Swal.fire({
        icon: "success",
        title: t("AddWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
      });
    }

    localStorage.setItem("wishlistItems", JSON.stringify(updatedWishlist));
    setWishlistItems(updatedWishlist);
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  // Filter out wishlist items that are valid (i.e., ensure `id-color` format is correct)
  const wishlistProducts = allProducts.flatMap((product) => {
    return wishlistItems
      .filter((item) => {
        // item'ın string olup olmadığını kontrol et
        return (
          typeof item === "string" &&
          (item === product.id || item.startsWith(`${product.id}-`))
        );
      })
      .map((item) => {
        return { product, selectedColor: item.split("-")[1] };
      });
  });

  const suggestionProducts = allProducts
    .filter(
      (product) =>
        !wishlistItems.some(
          (item) =>
            typeof item === "string" &&
            (item === product.id || item.startsWith(`${product.id}-`))
        )
    )
    .slice(0, 8);

  if (loading)
    return (
      <>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6 text-center">
              <div className="alert alert-warning" role="alert">
                Your wishlist is loading, please wait.
              </div>
            </div>
          </div>
        </div>
      </>
    );

  return (
    <div className="container-fluid">
      {wishlistProducts.length === 0 ? (
        <div className="pt-5">
          <div className="text-center">
            <h3 className="formtitleh1">{t("WishlistSuccessTitle")}</h3>
            <p className="formbtnfontsize fw-light">
              {t("WishlistSuccessDesc")}
            </p>
            <Link
              to="/userlogin"
              className="btn bgcolorfooterbottom p-2  pe-5 px-5 mb-5"
            >
              {t("WishlistSuccessBtn")}
            </Link>
          </div>
          <h6 className="fw-normal mx-5">{t("RecentlyViewed")}</h6>
          <div className="row mt-4">
            {suggestionProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                wishlistItems={wishlistItems}
                handleWishlistClick={handleWishlistClick}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="row">
          {wishlistProducts.map(({ product, selectedColor }) => (
            <ProductCard
              key={`${product.id}-${selectedColor || "default"}`}
              product={product}
              selectedColor={selectedColor}
              wishlistItems={wishlistItems}
              handleWishlistClick={handleWishlistClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductCard = ({
  product,
  wishlistItems,
  selectedColor,
  handleWishlistClick,
}) => {
  const colorVariants = product.color_images || []; // Color variants if available
  const isInWishlist = (productId, selectedColor) => {
    const key = selectedColor ? `${productId}-${selectedColor}` : productId;
    return wishlistItems.includes(key);
  };
  const { currency, conversionRate } = useCurrency();
  const { t } = useTranslation();
  return (
    <div className="col-12 col-md-6 col-lg-3 fade-in pe-0 px-0">
      <div className="card cardbg border-0 rounded-0">
        <div className="position-relative">
          <div className="d-flex justify-content-between">
            {/* Wishlist Button */}
            <Link
              onClick={handleWishlistClick(product.id, selectedColor)} // For default product (no color selected)
              className="wishlist-button position-absolute top-0 end-0 m-2"
            >
              <i
                className={`${
                  isInWishlist(product.id, selectedColor)
                    ? "fas fa-heart text-white"
                    : "far fa-heart text-white"
                } fa-lg wishlist-icon`}
              />
            </Link>

            {/* If product has colors, create a button for each color */}
            {colorVariants.length > 0 && (
              <div className="color-variants">
                {colorVariants.map((color) => (
                  <Link
                    key={color.name}
                    onClick={handleWishlistClick(product.id, color.name)}
                    className="color-button"
                    style={{
                      backgroundColor: color.name,
                    }}
                  >
                    <i
                      className={`${
                        isInWishlist(product.id, color.name)
                          ? "fas fa-heart text-white"
                          : "far fa-heart text-white"
                      } fa-lg wishlist-icon`}
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>
          {/* Carousel for product images */}
          <div
            id={`wishlist-carousel-${product.id}-${selectedColor || "default"}`}
            className="carousel slide"
            data-bs-ride="carousel"
          >
            <div className="carousel-inner">
              <Link to={`/product/${product.id}`}>
                {(selectedColor
                  ? product.color_images?.find((c) => c.name === selectedColor)
                      ?.images || []
                  : product.default_images
                ).map((img, index) => (
                  <div
                    className={`carousel-item ${index === 0 ? "active" : ""}`}
                    key={index}
                  >
                    <img
                      src={img}
                      className="d-block w-100"
                      alt={product.name}
                    />
                  </div>
                ))}
              </Link>
            </div>
            <button
              className="carousel-control-prev"
              type="button"
              data-bs-target={`#wishlist-carousel-${product.id}-${
                selectedColor || "default"
              }`}
              data-bs-slide="prev"
            >
              <i className="fa-solid fa-angle-left carousel-control-prev-icon"></i>
            </button>
            <button
              className="carousel-control-next"
              type="button"
              data-bs-target={`#wishlist-carousel-${product.id}-${
                selectedColor || "default"
              }`}
              data-bs-slide="next"
            >
              <i className="fa-solid fa-chevron-right carousel-control-next-icon"></i>
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <h5 className="mb-0 text-dark">{product.name}</h5>
              <p className="fw-normal fs-6">
                {(product.price / conversionRate)
                  .toFixed(3)
                  .replace(/^0\.0*/, "")}
                <span className="mx-1">{currency}</span>
              </p>
            </div>

            <Link
              to={`/product/${product.id}`}
              className="btn custom-btn d-flex align-items-center justify-content-center"
            >
              <img src={bagIcon} width={15} alt="Bag Icon" className="icon" />
              <span className="text ms-2">{t("Shop")}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
